import { KmxInfoCircleStroked } from '@kmx/mui-icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase, { type ButtonBaseProps } from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';

import DialogTitleWithCloseButton from '@/components/DialogTitleWithCloseButton/DialogTitleWithCloseButton';
import type { FinanceInformation } from '@/types/FinanceInformation';

import { BGCOLORS, TEXTCOLORS } from '../../../constants/fbsColorConstants';
import DECISIONTYPES from '../../../constants/fbsDecisionLabelTypeConstants';
import fbsDecisionLabelFunctions from '../../../helpers/fbsDecisionLabelFunctions';
import {
  getMaxApr,
  getMaxPayment,
  getMinApr,
  getMinPayment,
  getTermLength,
} from '../../../helpers/financeTermsHelpers';
import modalStatusConfigFunctions from '../../../helpers/modalStatusConfigFunctions';
import AvailableTerms from '../AvailableTerms/AvailableTerms';
import PriceTable from '../PriceTable/PriceTable';

type FinanceTermsProps = {
  basePrice: number;
  stockNumber: string;
  carIsUnavailable: boolean;
  financeInformation: FinanceInformation;
};

const Flag = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'bgcolor' && prop !== 'txtcolor',
})<ButtonBaseProps & { bgcolor: string; txtcolor: string }>(
  ({ theme, bgcolor, txtcolor }) => ({
    border: 0,
    width: '100%',
    borderRadius: 8,
    cursor: 'pointer',
    backgroundColor: bgcolor,
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

    svg: {
      color: txtcolor,
    },

    '& .MuiTypography-root': {
      color: txtcolor,
    },
  }),
);

const FinanceTerms = ({
  basePrice,
  stockNumber,
  carIsUnavailable,
  financeInformation,
}: FinanceTermsProps) => {
  const {
    feesList,
    fbsDecision,
    fbsApplication,
    currentDownPayment,
    isSmallBatchLoading,
    onFbsTermsModalShown,
    fbsModalButtonClickHandler,
  } = financeInformation;

  const minApr = getMinApr(fbsDecision);
  const maxApr = getMaxApr(fbsDecision);
  const minPayment = getMinPayment(fbsDecision);
  const maxPayment = getMaxPayment(fbsDecision);
  const termLength = getTermLength(fbsDecision);
  const carDetailPageLink = `/car/${stockNumber}`;
  const downPayment = fbsDecision?.downPayment ?? currentDownPayment;

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
    if (onFbsTermsModalShown) onFbsTermsModalShown();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const labelContent = useMemo(() => {
    if (carIsUnavailable || !fbsDecision) {
      const getLabelContentFn = fbsDecisionLabelFunctions.default;
      return getLabelContentFn();
    }

    const decisionKey =
      fbsDecision.decision.toLowerCase() as keyof typeof fbsDecisionLabelFunctions;
    const getLabelContentFn =
      fbsDecisionLabelFunctions[decisionKey] ||
      fbsDecisionLabelFunctions.default;
    return getLabelContentFn({ fbsDecision, downPayment });
  }, [carIsUnavailable, downPayment, fbsDecision]);

  const [flagBgColor, flagTextColor] = useMemo(() => {
    if (carIsUnavailable) {
      return [BGCOLORS.disabled, TEXTCOLORS.disabled];
    }

    switch (labelContent.type) {
      case DECISIONTYPES.additionalDown:
        return [BGCOLORS.additionalDown, TEXTCOLORS.additionalDown];
      case DECISIONTYPES.declined:
        return [BGCOLORS.declined, TEXTCOLORS.declined];
      case DECISIONTYPES.noInfo:
        return [BGCOLORS.noInfo, TEXTCOLORS.noInfo];
      case DECISIONTYPES.notNeeded:
        return [BGCOLORS.notNeeded, TEXTCOLORS.notNeeded];
      case DECISIONTYPES.preApproved:
        return [BGCOLORS.preApproved, TEXTCOLORS.preApproved];
      default:
        return [BGCOLORS.default, TEXTCOLORS.default];
    }
  }, [carIsUnavailable, labelContent.type]);

  const modalContent = useMemo(() => {
    if (carIsUnavailable || !fbsDecision) {
      const getModalContentFn = modalStatusConfigFunctions.default;
      return getModalContentFn();
    }

    const decisionKey =
      fbsDecision.decision.toLowerCase() as keyof typeof modalStatusConfigFunctions;
    const getModalContentFn =
      modalStatusConfigFunctions[decisionKey] ||
      modalStatusConfigFunctions.default;
    return getModalContentFn({ fbsDecision, downPayment });
  }, [carIsUnavailable, fbsDecision, downPayment]);

  const modalButtonClickHandler = () => {
    if (fbsModalButtonClickHandler) fbsModalButtonClickHandler();
  };

  //pad tile
  if (!fbsDecision && isSmallBatchLoading) {
    return (
      <Box
        className="kmx-car-tile__finance-terms-pending"
        sx={{ visibility: 'hidden' }}
      >
        <Typography variant="h6"> &nbsp; </Typography>
        <Typography variant="caption"> &nbsp; </Typography>
        <Flag bgcolor="transparent" txtcolor="transparent">
          <Stack direction="row">
            <Typography variant="caption"> &nbsp; </Typography>
            <KmxInfoCircleStroked fontSize="inherit" />
          </Stack>
        </Flag>
      </Box>
    );
  }

  return (
    <Box className="kmx-car-tile__finance-terms">
      {labelContent.hasTerms ? (
        <>
          <Typography variant="h6" component="span" className="monthly-payment">
            {minPayment === maxPayment
              ? `${minPayment}/mo`
              : `${minPayment}-${maxPayment}/mo`}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            divider={
              <Divider
                flexItem
                orientation="vertical"
                sx={{ alignSelf: 'center', height: '10px' }}
              />
            }
            className="apr-and-term-length"
          >
            <Typography variant="caption" color="textSecondary" className="apr">
              {minApr === maxApr
                ? `${minApr}% APR`
                : `${minApr}%-${maxApr}% APR`}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              className="term-length"
            >
              {termLength} months
            </Typography>
          </Stack>
        </>
      ) : (
        <div className="top-margin-for-no-terms">
          <Typography variant="h6"> &nbsp; </Typography>
          <Typography variant="caption"> &nbsp; </Typography>
        </div>
      )}

      <Flag
        bgcolor={flagBgColor}
        txtcolor={flagTextColor}
        onClick={handleClickOpen}
        className="kmx-car-tile__monthly-payment-flag"
      >
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption"> {labelContent.text} </Typography>
          <KmxInfoCircleStroked fontSize="inherit" />
        </Stack>
      </Flag>

      <Dialog
        open={open}
        onClose={handleClose}
        data-is-modal="true"
        className="kmx-car-tile__fbs-terms-modal"
      >
        <DialogTitleWithCloseButton variant="h5" onClose={handleClose}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {modalContent.icon}
            {typeof modalContent.title === 'string' ? (
              <span> {modalContent.title} </span>
            ) : (
              modalContent.title
            )}
          </Stack>
        </DialogTitleWithCloseButton>

        <DialogContent>
          {fbsDecision && modalContent.hasTerms ? (
            <>
              {fbsDecision.terms && fbsDecision.terms.length >= 1 && (
                <AvailableTerms fbsDecision={fbsDecision} />
              )}
              <PriceTable
                fbsApplication={fbsApplication}
                fbsDecision={fbsDecision}
                downPayment={downPayment}
                basePrice={basePrice}
                feesList={feesList}
              />

              <Button
                sx={{ my: 2 }}
                variant="contained"
                color="primary"
                href={carDetailPageLink}
                onClick={modalButtonClickHandler}
                data-ignore-anchor-tag-rule
              >
                View All Car Details
              </Button>
            </>
          ) : (
            <>{modalContent.body}</>
          )}
          {modalContent.disclaimerText}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FinanceTerms;
